import React from 'react';
import { isGoogleCalendarConfigured, getGoogleCalendarConfig } from '../services/googleCalendar';
import { FiCheckCircle, FiXCircle, FiExternalLink, FiInfo } from 'react-icons/fi';

interface GoogleCalendarSetupProps {
  onClose?: () => void;
}

export default function GoogleCalendarSetup({ onClose }: GoogleCalendarSetupProps) {
  const isConfigured = isGoogleCalendarConfigured();
  const config = getGoogleCalendarConfig();

  const checks = [
    {
      name: 'API Key configurada',
      status: config.apiKey && config.apiKey !== 'your_api_key_here',
      value: config.apiKey ? (config.apiKey === 'your_api_key_here' ? 'Pendiente' : '✓ Configurada') : 'No configurada'
    },
    {
      name: 'Client ID configurado',
      status: config.clientId && config.clientId !== 'your_client_id_here',
      value: config.clientId ? (config.clientId === 'your_client_id_here' ? 'Pendiente' : '✓ Configurado') : 'No configurado'
    },
    {
      name: 'Calendar ID',
      status: true, // Siempre true porque tiene default
      value: config.calendarId
    },
    {
      name: 'Redirect URI',
      status: true, // Siempre true porque tiene default
      value: config.redirectUri
    }
  ];

  return (
    <div className="google-calendar-setup panel pad-lg" style={{ maxWidth: 600 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          📅 Configuración Google Calendar
          {isConfigured ? (
            <FiCheckCircle style={{ color: 'var(--success, #22c55e)' }} />
          ) : (
            <FiXCircle style={{ color: 'var(--error, #ef4444)' }} />
          )}
        </h3>
        {onClose && (
          <button onClick={onClose} className="btn-ghost" style={{ padding: 4 }}>
            ✕
          </button>
        )}
      </div>

      {isConfigured ? (
        <div style={{ marginBottom: 20 }}>
          <div className="success-message" style={{
            background: 'var(--success-bg, #dcfce7)',
            color: 'var(--success, #22c55e)',
            padding: 12,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <FiCheckCircle />
            ¡Google Calendar está configurado correctamente!
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: 20 }}>
          <div className="warning-message" style={{
            background: 'var(--warning-bg, #fefce8)',
            color: 'var(--warning, #eab308)',
            padding: 12,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <FiInfo />
            Google Calendar necesita configuración para funcionar completamente.
          </div>
        </div>
      )}

      <div className="config-status" style={{ marginBottom: 20 }}>
        <h4 style={{ fontSize: 16, marginBottom: 12 }}>Estado de configuración:</h4>
        {checks.map((check, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 0',
              borderBottom: index < checks.length - 1 ? '1px solid var(--divider, rgba(0,0,0,0.1))' : 'none'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {check.status ? (
                <FiCheckCircle style={{ color: 'var(--success, #22c55e)', fontSize: 16 }} />
              ) : (
                <FiXCircle style={{ color: 'var(--error, #ef4444)', fontSize: 16 }} />
              )}
              <span>{check.name}</span>
            </div>
            <span style={{
              fontSize: 14,
              color: check.status ? 'var(--success, #22c55e)' : 'var(--muted, #6b7280)',
              fontFamily: 'monospace'
            }}>
              {check.value}
            </span>
          </div>
        ))}
      </div>

      {!isConfigured && (
        <div className="setup-instructions">
          <h4 style={{ fontSize: 16, marginBottom: 12 }}>📋 Para configurar Google Calendar:</h4>
          <ol style={{ paddingLeft: 20, marginBottom: 16 }}>
            <li>Lee la guía <code>GOOGLE_SETUP.md</code> en la raíz del proyecto</li>
            <li>Ve a <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a></li>
            <li>Crea un proyecto y habilita Calendar API</li>
            <li>Obtén API Key y Client ID</li>
            <li>Actualiza el archivo <code>.env.local</code></li>
          </ol>
          
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <a
              href="https://console.cloud.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
            >
              <FiExternalLink size={16} />
              Google Cloud Console
            </a>
          </div>
        </div>
      )}

      {isConfigured && (
        <div className="configured-info">
          <h4 style={{ fontSize: 16, marginBottom: 12 }}>🎉 Funcionalidades disponibles:</h4>
          <ul style={{ paddingLeft: 20, color: 'var(--muted, #6b7280)' }}>
            <li>Ver eventos de todos tus calendarios de Google</li>
            <li>Sincronizar tareas como eventos de calendario</li>
            <li>Crear y editar eventos desde TaskManager</li>
            <li>Autenticación automática con Google</li>
          </ul>
          
          <div style={{ marginTop: 16, padding: 12, background: 'var(--surface, #f8fafc)', borderRadius: 6 }}>
            <small style={{ color: 'var(--muted, #6b7280)' }}>
              💡 <strong>Tip:</strong> Ve a tu perfil para conectar tu cuenta de Google y comenzar a usar el calendario.
            </small>
          </div>
        </div>
      )}
    </div>
  );
}